import React, { useEffect, useState } from 'react';
import { BookOpen, Pin, Eye, ShoppingCart, DollarSign, TrendingUp, Clock, Activity, Zap, ChevronRight, UserCheck, ArrowUp, ArrowDown, Play, Pause, Trash2, Calendar, CreditCard, Info, CheckCircle2, LayoutList, ExternalLink, Database, Server, History, Receipt, AlertCircle, RefreshCw, Mail, Settings, X, BarChart3, MousePointer, Sparkles, SplitSquareHorizontal, Edit, Target, Timer, ArrowRight, AlertTriangle, Layers } from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
interface FeaturedCoursesManagerProps {
  courses: any[];
  setDetailPanel: (panel: any) => void;
  success: (msg: string) => void;
}
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0
  }).format(amount);
};
// Mock Audit Logs
const initialLogs = [{
  id: 'LOG-1',
  time: '10:42:15',
  type: 'auto_swap',
  message: 'Авто-замена: FC-01 завершен. Активирован FC-03.',
  status: 'success'
}, {
  id: 'LOG-2',
  time: '10:41:02',
  type: 'payment',
  message: 'Оплата подтверждена для FC-02. Статус изменен на "Активен".',
  status: 'success'
}, {
  id: 'LOG-3',
  time: '10:30:00',
  type: 'notification',
  message: 'Отправлено 45 SMS-уведомлений о старте кампаний.',
  status: 'info'
}];
export function FeaturedCoursesManager({
  courses: initialCourses,
  setDetailPanel,
  success
}: FeaturedCoursesManagerProps) {
  const [campaigns, setCampaigns] = useState(initialCourses);
  const [activeModal, setActiveModal] = useState<'logs' | 'payment' | 'timing' | 'analytics' | 'ab_test' | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<any | null>(null);
  const [logs, setLogs] = useState(initialLogs);
  const [engineStatus, setEngineStatus] = useState<'active' | 'degraded' | 'error'>('active');
  // Sync with props
  useEffect(() => {
    setCampaigns(initialCourses);
  }, [initialCourses]);
  const addLog = (type: string, message: string, status: string) => {
    setLogs(prev => [{
      id: `LOG-${Date.now()}`,
      time: new Date().toLocaleTimeString('ru-RU'),
      type,
      message,
      status
    }, ...prev].slice(0, 50));
  };
  const handleAction = (action: string, campaign: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    switch (action) {
      case 'view_course':
        window.open(`/course/${campaign.id}`, '_blank');
        break;
      case 'view_admin':
        setDetailPanel({
          type: 'featured-course',
          title: campaign.course,
          data: campaign
        });
        break;
      case 'view_advertiser':
        setDetailPanel({
          type: 'user-profile',
          title: campaign.advertiser,
          data: {
            name: campaign.advertiser,
            type: campaign.advertiserType
          }
        });
        break;
      case 'view_slot':
        setDetailPanel({
          type: 'slot-detail',
          title: campaign.slot,
          data: {
            name: campaign.slot
          }
        });
        break;
      case 'analytics':
        setSelectedCampaign(campaign);
        setActiveModal('analytics');
        break;
      case 'payment':
        setSelectedCampaign(campaign);
        setActiveModal('payment');
        break;
      case 'timing':
        setSelectedCampaign(campaign);
        setActiveModal('timing');
        break;
      case 'ab_test':
        setSelectedCampaign(campaign);
        setActiveModal('ab_test');
        break;
      case 'activate':
        if (campaign.paymentStatus !== 'paid') {
          addLog('error', `Отказ активации ${campaign.id}: Требуется оплата`, 'error');
          alert('Невозможно активировать: требуется оплата');
          return;
        }
        success(`Кампания ${campaign.id} активирована`);
        addLog('activation', `Кампания ${campaign.id} успешно активирована`, 'success');
        setCampaigns(prev => prev.map(c => c.id === campaign.id ? {
          ...c,
          status: 'active'
        } : c));
        break;
      case 'pause':
        success(`Кампания ${campaign.id} приостановлена`);
        addLog('pause', `Кампания ${campaign.id} поставлена на паузу`, 'warning');
        setCampaigns(prev => prev.map(c => c.id === campaign.id ? {
          ...c,
          status: 'paused'
        } : c));
        break;
      case 'fastTrack':
        success(`Fast Track включен для ${campaign.id}`);
        addLog('fast_track', `Fast Track включен для ${campaign.id}`, 'info');
        break;
      case 'delete':
        if (confirm(`Удалить кампанию ${campaign.id}?`)) {
          success(`Кампания ${campaign.id} удалена`);
          setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
        }
        break;
    }
  };
  // --- METRICS CALCULATION ---
  const totalImpressions = campaigns.reduce((acc, c) => acc + (c.impressions || 0), 0);
  const totalClicks = campaigns.reduce((acc, c) => acc + (c.clicks || 0), 0);
  const totalPurchases = campaigns.reduce((acc, c) => acc + (c.purchases || 0), 0);
  const totalRevenue = campaigns.reduce((acc, c) => acc + (c.status === 'active' ? c.price : 0), 0);
  const totalCourseRevenue = totalPurchases * 15000; // Mock avg check
  const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions * 100).toFixed(2) : '0.00';
  const avgCr = totalClicks > 0 ? (totalPurchases / totalClicks * 100).toFixed(2) : '0.00';
  const avgCpa = totalPurchases > 0 ? Math.round(totalRevenue / totalPurchases) : 0;
  const avgRpm = totalImpressions > 0 ? Math.round(totalRevenue / totalImpressions * 1000) : 0;
  const renderKpis = () => <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6" data-id="element-12400">
      <Card padding="sm" hoverable className="bg-indigo-50 border-indigo-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-12401">
        <div className="text-[10px] text-indigo-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-12402">
          <Pin className="w-3 h-3" data-id="element-12403" /> Активные
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-12404">
          {campaigns.filter(c => c.status === 'active').length}
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-blue-50 border-blue-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-12405">
        <div className="text-[10px] text-blue-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-12406">
          <Eye className="w-3 h-3" data-id="element-12407" /> Показы
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-12408">
          {totalImpressions.toLocaleString()}
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-sky-50 border-sky-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-12409">
        <div className="text-[10px] text-sky-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-12410">
          <MousePointer className="w-3 h-3" data-id="element-12411" /> Клики
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-12412">
          {totalClicks.toLocaleString()}
        </div>
        <div className="text-[10px] text-sky-600 mt-1" data-id="element-12413">CTR {avgCtr}%</div>
      </Card>
      <Card padding="sm" hoverable className="bg-emerald-50 border-emerald-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-12414">
        <div className="text-[10px] text-emerald-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-12415">
          <ShoppingCart className="w-3 h-3" data-id="element-12416" /> Покупки
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-12417">
          {totalPurchases.toLocaleString()}
        </div>
        <div className="text-[10px] text-emerald-600 mt-1" data-id="element-12418">CR {avgCr}%</div>
      </Card>
      <Card padding="sm" hoverable className="bg-green-50 border-green-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-12419">
        <div className="text-[10px] text-green-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-12420">
          <DollarSign className="w-3 h-3" data-id="element-12421" /> Доход (Ads)
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-12422">
          {formatCurrency(totalRevenue)}
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-amber-50 border-amber-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-12423">
        <div className="text-[10px] text-amber-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-12424">
          <Target className="w-3 h-3" data-id="element-12425" /> CPA
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-12426">
          {formatCurrency(avgCpa)}
        </div>
        <div className="text-[10px] text-amber-600 mt-1" data-id="element-12427">Цена за покупку</div>
      </Card>
      <Card padding="sm" hoverable className="bg-purple-50 border-purple-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-12428">
        <div className="text-[10px] text-purple-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-12429">
          <Activity className="w-3 h-3" data-id="element-12430" /> RPM
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-12431">
          {formatCurrency(avgRpm)}
        </div>
        <div className="text-[10px] text-purple-600 mt-1" data-id="element-12432">
          Доход на 1000 пок.
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-slate-50 border-slate-200 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-12433">
        <div className="text-[10px] text-slate-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-12434">
          <CreditCard className="w-3 h-3" data-id="element-12435" /> Ср. чек курса
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-12436">
          {formatCurrency(15000)}
        </div>
      </Card>
    </div>;
  const renderAiRecommendations = () => <Card padding="sm" className="mb-6 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50" data-id="element-12437">
      <div className="flex items-start gap-4" data-id="element-12438">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0" data-id="element-12439">
          <Sparkles className="w-5 h-5 text-indigo-600" data-id="element-12440" />
        </div>
        <div className="flex-1" data-id="element-12441">
          <h3 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-12442">
            AI Рекомендации для платформы
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-12443">
            <div className="bg-white/60 p-3 rounded-lg border border-indigo-100" data-id="element-12444">
              <div className="text-xs font-bold text-slate-800 mb-1" data-id="element-12445">
                Повышение цены слота
              </div>
              <div className="text-[10px] text-slate-600" data-id="element-12446">
                Слот "Главная страница - TOP" перегружен (очередь 4+).
                Рекомендуется повысить базовую цену на 15% для балансировки
                спроса.
              </div>
            </div>
            <div className="bg-white/60 p-3 rounded-lg border border-indigo-100" data-id="element-12447">
              <div className="text-xs font-bold text-slate-800 mb-1" data-id="element-12448">
                Оптимизация креативов
              </div>
              <div className="text-[10px] text-slate-600" data-id="element-12449">
                Кампания FC-02 имеет CTR ниже среднего (3.4%). Предложите
                рекламодателю запустить A/B тест с новым креативом.
              </div>
            </div>
            <div className="bg-white/60 p-3 rounded-lg border border-indigo-100" data-id="element-12450">
              <div className="text-xs font-bold text-slate-800 mb-1" data-id="element-12451">
                Upsell возможность
              </div>
              <div className="text-[10px] text-slate-600" data-id="element-12452">
                Внешний рекламодатель "Институт Астрологии" имеет высокий
                бюджет. Предложите пакетное размещение "Сайдбар + Email".
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>;
  const renderTable = () => <Card padding="none" className="rounded-xl shadow-sm border-slate-200 overflow-hidden" data-id="element-12453">
      <div className="p-4 border-b border-slate-200 bg-white flex justify-between items-center" data-id="element-12454">
        <div className="flex items-center gap-3" data-id="element-12455">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center" data-id="element-12456">
            <BookOpen className="w-4 h-4 text-indigo-600" data-id="element-12457" />
          </div>
          <div data-id="element-12458">
            <h3 className="text-sm font-bold text-slate-900" data-id="element-12459">
              Управление кампаниями курсов
            </h3>
            <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-12460">
              Ads Manager для образовательных продуктов
            </p>
          </div>
        </div>
        <div className="flex gap-2" data-id="element-12461">
          <Button variant="outline" size="sm" icon={<History className="w-4 h-4" data-id="element-12463" />} onClick={() => setActiveModal('logs')} data-id="element-12462">
            Audit Logs
          </Button>
          <Button variant="primary" size="sm" icon={<Pin className="w-4 h-4" data-id="element-12465" />} data-id="element-12464">
            Создать кампанию
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-12466">
        <table className="w-full text-left whitespace-nowrap" data-id="element-12467">
          <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wider" data-id="element-12468">
            <tr data-id="element-12469">
              <th className="px-4 py-3 font-medium" data-id="element-12470">Кампания / Курс</th>
              <th className="px-4 py-3 font-medium" data-id="element-12471">Рекламодатель</th>
              <th className="px-4 py-3 font-medium" data-id="element-12472">Слот / Позиция</th>
              <th className="px-4 py-3 font-medium" data-id="element-12473">Таймер / Период</th>
              <th className="px-4 py-3 font-medium" data-id="element-12474">Коммерция</th>
              <th className="px-4 py-3 font-medium" data-id="element-12475">
                Воронка (Показы → Клики → Покупки)
              </th>
              <th className="px-4 py-3 font-medium" data-id="element-12476">Статус / Очередь</th>
              <th className="px-4 py-3 text-right font-medium" data-id="element-12477">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-12478">
            {campaigns.map(c => {
            const ctr = c.impressions > 0 ? (c.clicks / c.impressions * 100).toFixed(1) : '0.0';
            const cr = c.clicks > 0 ? (c.purchases / c.clicks * 100).toFixed(1) : '0.0';
            const isPaid = c.paymentStatus === 'paid';
            // Timer logic simulation
            let timerColor = 'text-slate-500';
            let timerText = 'Ожидание';
            if (c.status === 'active') {
              timerColor = 'text-emerald-600';
              timerText = 'Осталось 14 дн.';
            } else if (c.status === 'queued') {
              timerColor = 'text-amber-600';
              timerText = 'Старт через 4 дн.';
            }
            return <tr key={c.id} className="hover:bg-slate-50 transition-colors group bg-white" data-id="element-12479">
                  {/* Course Info */}
                  <td className="px-4 py-3" data-id="element-12480">
                    <div className="flex items-center gap-2 mb-1" data-id="element-12481">
                      <span className="text-[10px] font-mono font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded cursor-pointer hover:underline" onClick={() => handleAction('view_admin', c)} data-id="element-12482">
                        {c.id}
                      </span>
                      {c.creatives?.length > 1 && <Badge variant="pro" size="sm" className="text-[9px] py-0 cursor-pointer" onClick={() => handleAction('ab_test', c)} data-id="element-12483">
                          <SplitSquareHorizontal className="w-2.5 h-2.5 mr-0.5" data-id="element-12484" />{' '}
                          A/B Test
                        </Badge>}
                    </div>
                    <div className="text-sm font-bold text-slate-900 cursor-pointer hover:text-indigo-600 flex items-center gap-1" onClick={() => handleAction('view_course', c)} data-id="element-12485">
                      {c.course} <ExternalLink className="w-3 h-3 opacity-50" data-id="element-12486" />
                    </div>
                  </td>

                  {/* Advertiser */}
                  <td className="px-4 py-3" data-id="element-12487">
                    <div className="text-xs font-medium text-slate-900 cursor-pointer hover:text-indigo-600 flex items-center gap-1.5" onClick={() => handleAction('view_advertiser', c)} data-id="element-12488">
                      <UserCheck className="w-3.5 h-3.5 text-slate-400" data-id="element-12489" />
                      {c.advertiser}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-12490">
                      {c.advertiserType === 'astrologer' ? 'Астролог платформы' : 'Внешний рекламодатель'}
                    </div>
                  </td>

                  {/* Slot & Position */}
                  <td className="px-4 py-3" data-id="element-12491">
                    <div className="text-xs font-bold text-slate-900 cursor-pointer hover:text-indigo-600 flex items-center gap-1.5 mb-1" onClick={() => handleAction('view_slot', c)} data-id="element-12492">
                      <LayoutList className="w-3.5 h-3.5 text-slate-400" data-id="element-12493" />
                      {c.slot}
                    </div>
                    <Badge variant="neutral" size="sm" className="text-[10px]" data-id="element-12494">
                      {c.position}
                    </Badge>
                  </td>

                  {/* Timer & Period */}
                  <td className="px-4 py-3" data-id="element-12495">
                    <div className={`text-xs font-bold flex items-center gap-1.5 mb-1 cursor-pointer hover:underline ${timerColor}`} onClick={() => handleAction('timing', c)} data-id="element-12496">
                      <Timer className="w-3.5 h-3.5" data-id="element-12497" />
                      {timerText}
                    </div>
                    <div className="text-[10px] text-slate-500 flex items-center gap-1" data-id="element-12498">
                      <Calendar className="w-3 h-3" data-id="element-12499" />
                      {c.periodStart} — {c.periodEnd}
                    </div>
                  </td>

                  {/* Commerce */}
                  <td className="px-4 py-3" data-id="element-12500">
                    <div className="text-sm font-bold text-slate-900 mb-1" data-id="element-12501">
                      {formatCurrency(c.price)}
                    </div>
                    <div className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded cursor-pointer transition-colors ${isPaid ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`} onClick={() => handleAction('payment', c)} data-id="element-12502">
                      <Receipt className="w-2.5 h-2.5" data-id="element-12503" />
                      {isPaid ? 'Оплачено' : 'Ожидает оплаты'}
                    </div>
                  </td>

                  {/* Funnel Metrics */}
                  <td className="px-4 py-3" data-id="element-12504">
                    <div className="flex items-center gap-4 cursor-pointer hover:bg-slate-50 p-1 rounded transition-colors" onClick={() => handleAction('analytics', c)} data-id="element-12505">
                      <div className="flex flex-col items-center w-16" data-id="element-12506">
                        <span className="text-xs font-bold text-slate-900" data-id="element-12507">
                          {c.impressions.toLocaleString()}
                        </span>
                        <span className="text-[9px] text-slate-500 uppercase" data-id="element-12508">
                          Показы
                        </span>
                      </div>
                      <ArrowRight className="w-3 h-3 text-slate-300" data-id="element-12509" />
                      <div className="flex flex-col items-center w-16" data-id="element-12510">
                        <span className="text-xs font-bold text-blue-600" data-id="element-12511">
                          {c.clicks.toLocaleString()}
                        </span>
                        <span className="text-[9px] text-slate-500 uppercase" data-id="element-12512">
                          CTR {ctr}%
                        </span>
                      </div>
                      <ArrowRight className="w-3 h-3 text-slate-300" data-id="element-12513" />
                      <div className="flex flex-col items-center w-16" data-id="element-12514">
                        <span className="text-xs font-bold text-emerald-600" data-id="element-12515">
                          {c.purchases.toLocaleString()}
                        </span>
                        <span className="text-[9px] text-slate-500 uppercase" data-id="element-12516">
                          CR {cr}%
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Status & Queue */}
                  <td className="px-4 py-3" data-id="element-12517">
                    <div className="flex flex-col gap-1.5 items-start" data-id="element-12518">
                      <Badge variant={c.status === 'active' ? 'success' : c.status === 'queued' ? 'warning' : 'neutral'} size="sm" data-id="element-12519">
                        {c.status === 'active' ? 'Транслируется' : c.status === 'queued' ? 'В очереди' : 'Пауза'}
                      </Badge>

                      {c.queuePosition && <div className="text-[10px] text-indigo-600 font-medium bg-indigo-50 px-1.5 py-0.5 rounded cursor-pointer hover:bg-indigo-100 flex items-center gap-1" onClick={() => handleAction('timing', c)} data-id="element-12520">
                          <Layers className="w-3 h-3" data-id="element-12521" />
                          Очередь: #{c.queuePosition}
                        </div>}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-right" data-id="element-12522">
                    <div className="flex items-center justify-end gap-1" data-id="element-12523">
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-slate-400 hover:text-indigo-600" title="Аналитика" onClick={() => handleAction('analytics', c)} data-id="element-12524">
                        <BarChart3 className="w-3.5 h-3.5" data-id="element-12525" />
                      </Button>
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-slate-400 hover:text-indigo-600" title="Редактировать" onClick={() => handleAction('view_admin', c)} data-id="element-12526">
                        <Edit className="w-3.5 h-3.5" data-id="element-12527" />
                      </Button>
                      {c.status === 'active' ? <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-amber-500 hover:bg-amber-50" title="Пауза" onClick={() => handleAction('pause', c)} data-id="element-12528">
                          <Pause className="w-3.5 h-3.5" data-id="element-12529" />
                        </Button> : <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-emerald-500 hover:bg-emerald-50" title="Активировать" onClick={e => handleAction('activate', c, e)} data-id="element-12530">
                          <Play className="w-3.5 h-3.5 fill-current" data-id="element-12531" />
                        </Button>}
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-rose-400 hover:text-rose-600 hover:bg-rose-50" title="Удалить" onClick={() => handleAction('delete', c)} data-id="element-12532">
                        <Trash2 className="w-3.5 h-3.5" data-id="element-12533" />
                      </Button>
                    </div>
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </Card>;
  const renderModals = () => {
    if (!activeModal) return null;
    return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={e => {
      if (e.target === e.currentTarget) setActiveModal(null);
    }} data-id="element-12534">
        {/* LOGS MODAL */}
        {activeModal === 'logs' && <Card className="w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-12535">
            <div className="p-4 border-b border-slate-200 bg-slate-900 text-white flex justify-between items-center shrink-0" data-id="element-12536">
              <div className="flex items-center gap-3" data-id="element-12537">
                <Server className="w-5 h-5 text-indigo-400" data-id="element-12538" />
                <div data-id="element-12539">
                  <h3 className="font-bold" data-id="element-12540">Audit Logs: Ads Manager</h3>
                  <p className="text-[10px] text-slate-400" data-id="element-12541">
                    Журнал событий рекламной платформы
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" icon={<X className="w-5 h-5" data-id="element-12543" />} onClick={() => setActiveModal(null)} data-id="element-12542" />
            </div>
            <div className="p-0 overflow-y-auto bg-slate-950 flex-1 font-mono text-xs" data-id="element-12544">
              {logs.map(log => <div key={log.id} className="flex gap-4 p-3 border-b border-slate-800 hover:bg-slate-900/50" data-id="element-12545">
                  <div className="text-slate-500 shrink-0" data-id="element-12546">{log.time}</div>
                  <div className={`shrink-0 w-24 ${log.status === 'error' ? 'text-rose-400' : log.status === 'warning' ? 'text-amber-400' : log.status === 'success' ? 'text-emerald-400' : 'text-blue-400'}`} data-id="element-12547">
                    [{log.type.toUpperCase()}]
                  </div>
                  <div className="text-slate-300" data-id="element-12548">{log.message}</div>
                </div>)}
            </div>
          </Card>}

        {/* PAYMENT MODAL */}
        {activeModal === 'payment' && selectedCampaign && <Card className="w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-12549">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-12550">
              <div className="flex items-center gap-2" data-id="element-12551">
                <Receipt className="w-5 h-5 text-indigo-600" data-id="element-12552" />
                <h3 className="font-bold text-slate-900" data-id="element-12553">
                  Детали оплаты кампании
                </h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-12555" />} onClick={() => setActiveModal(null)} data-id="element-12554" />
            </div>
            <div className="p-6 space-y-6" data-id="element-12556">
              <div className="text-center" data-id="element-12557">
                <div className="text-sm text-slate-500 mb-1" data-id="element-12558">
                  Сумма к оплате
                </div>
                <div className="text-3xl font-bold text-slate-900" data-id="element-12559">
                  {formatCurrency(selectedCampaign.price || 0)}
                </div>
                <Badge variant={selectedCampaign.paymentStatus === 'paid' ? 'success' : 'warning'} className="mt-2" data-id="element-12560">
                  {selectedCampaign.paymentStatus === 'paid' ? 'Оплачено' : 'Ожидает оплаты'}
                </Badge>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 space-y-3 text-sm" data-id="element-12561">
                <div className="flex justify-between" data-id="element-12562">
                  <span className="text-slate-500" data-id="element-12563">ID Кампании</span>
                  <span className="font-bold text-slate-900" data-id="element-12564">
                    {selectedCampaign.id}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-12565">
                  <span className="text-slate-500" data-id="element-12566">Инвойс</span>
                  <span className="font-mono text-indigo-600" data-id="element-12567">
                    {selectedCampaign.invoiceId || 'INV-PENDING'}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-12568">
                  <span className="text-slate-500" data-id="element-12569">Транзакция</span>
                  <span className="font-mono text-slate-900" data-id="element-12570">
                    {selectedCampaign.paymentId || '—'}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-12571">
                  <span className="text-slate-500" data-id="element-12572">Рекламодатель</span>
                  <span className="font-medium text-slate-900" data-id="element-12573">
                    {selectedCampaign.advertiser}
                  </span>
                </div>
              </div>

              {selectedCampaign.paymentStatus !== 'paid' && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3" data-id="element-12574">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" data-id="element-12575" />
                  <div className="text-sm text-amber-800" data-id="element-12576">
                    <strong data-id="element-12577">Блокировка активации:</strong> Кампания не будет
                    запущена до подтверждения оплаты.
                  </div>
                </div>}

              <div className="flex gap-3 pt-4 border-t border-slate-100" data-id="element-12578">
                <Button variant="outline" className="flex-1" onClick={() => setActiveModal(null)} data-id="element-12579">
                  Закрыть
                </Button>
                {selectedCampaign.paymentStatus !== 'paid' && <Button variant="primary" className="flex-1" onClick={() => {
              success('Оплата подтверждена');
              addLog('payment', `Ручное подтверждение оплаты для ${selectedCampaign.id}`, 'success');
              setCampaigns(prev => prev.map(c => c.id === selectedCampaign.id ? {
                ...c,
                paymentStatus: 'paid',
                paymentId: 'PAY-MANUAL-' + Date.now()
              } : c));
              setActiveModal(null);
            }} data-id="element-12580">
                    Подтвердить оплату
                  </Button>}
              </div>
            </div>
          </Card>}

        {/* TIMING MODAL */}
        {activeModal === 'timing' && selectedCampaign && <Card className="w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-12581">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-12582">
              <div className="flex items-center gap-2" data-id="element-12583">
                <Clock className="w-5 h-5 text-indigo-600" data-id="element-12584" />
                <h3 className="font-bold text-slate-900" data-id="element-12585">Тайминг и Очередь</h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-12587" />} onClick={() => setActiveModal(null)} data-id="element-12586" />
            </div>
            <div className="p-6 space-y-4 text-sm" data-id="element-12588">
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-12589">
                <span className="text-slate-500" data-id="element-12590">Статус</span>
                <Badge variant={selectedCampaign.status === 'active' ? 'success' : 'warning'} data-id="element-12591">
                  {selectedCampaign.status === 'active' ? 'Транслируется' : 'В очереди'}
                </Badge>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-12592">
                <span className="text-slate-500" data-id="element-12593">Период</span>
                <span className="font-medium text-slate-900" data-id="element-12594">
                  {selectedCampaign.periodStart} — {selectedCampaign.periodEnd}
                </span>
              </div>

              {selectedCampaign.status === 'queued' && <>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-12595">
                    <span className="text-slate-500" data-id="element-12596">Позиция в очереди</span>
                    <span className="font-bold text-indigo-600" data-id="element-12597">
                      #{selectedCampaign.queuePosition}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-12598">
                    <span className="text-slate-500" data-id="element-12599">Ожидаемый старт</span>
                    <span className="font-bold text-slate-900" data-id="element-12600">
                      {selectedCampaign.expectedStartAt || 'Неизвестно'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-12601">
                    <span className="text-slate-500" data-id="element-12602">Время ожидания</span>
                    <span className="font-medium text-amber-600" data-id="element-12603">
                      {selectedCampaign.waitTime || '—'}
                    </span>
                  </div>
                </>}

              <div className="mt-6 bg-indigo-50 p-4 rounded-xl text-xs text-indigo-800 flex gap-2" data-id="element-12604">
                <Info className="w-4 h-4 shrink-0 mt-0.5" data-id="element-12605" />
                <p data-id="element-12606">
                  Система автоматически активирует кампанию при освобождении
                  слота "{selectedCampaign.slot}" (onExpire event).
                </p>
              </div>
            </div>
          </Card>}

        {/* ANALYTICS MODAL */}
        {activeModal === 'analytics' && selectedCampaign && <Card className="w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-12607">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-12608">
              <div className="flex items-center gap-2" data-id="element-12609">
                <BarChart3 className="w-5 h-5 text-indigo-600" data-id="element-12610" />
                <h3 className="font-bold text-slate-900" data-id="element-12611">
                  Аналитика кампании: {selectedCampaign.course}
                </h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-12613" />} onClick={() => setActiveModal(null)} data-id="element-12612" />
            </div>
            <div className="p-6" data-id="element-12614">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-12615">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-12616">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-12617">Показы</div>
                  <div className="text-2xl font-bold text-slate-900" data-id="element-12618">
                    {selectedCampaign.impressions.toLocaleString()}
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-12619">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-12620">Клики</div>
                  <div className="text-2xl font-bold text-blue-600" data-id="element-12621">
                    {selectedCampaign.clicks.toLocaleString()}
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-12622">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-12623">CTR</div>
                  <div className="text-2xl font-bold text-indigo-600" data-id="element-12624">
                    {selectedCampaign.impressions > 0 ? (selectedCampaign.clicks / selectedCampaign.impressions * 100).toFixed(2) : '0.00'}
                    %
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-12625">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-12626">Покупки</div>
                  <div className="text-2xl font-bold text-emerald-600" data-id="element-12627">
                    {selectedCampaign.purchases.toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="h-48 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-center mb-6" data-id="element-12628">
                <div className="text-slate-400 flex items-center gap-2" data-id="element-12629">
                  <BarChart3 className="w-5 h-5" data-id="element-12630" />
                  <span data-id="element-12631">График конверсий (Mock)</span>
                </div>
              </div>

              <div className="flex justify-end" data-id="element-12632">
                <Button variant="outline" onClick={() => setActiveModal(null)} data-id="element-12633">
                  Закрыть
                </Button>
              </div>
            </div>
          </Card>}

        {/* A/B TEST MODAL */}
        {activeModal === 'ab_test' && selectedCampaign && <Card className="w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-12634">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-12635">
              <div className="flex items-center gap-2" data-id="element-12636">
                <SplitSquareHorizontal className="w-5 h-5 text-indigo-600" data-id="element-12637" />
                <h3 className="font-bold text-slate-900" data-id="element-12638">
                  A/B Тестирование креативов
                </h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-12640" />} onClick={() => setActiveModal(null)} data-id="element-12639" />
            </div>
            <div className="p-6" data-id="element-12641">
              <p className="text-sm text-slate-600 mb-6" data-id="element-12642">
                Сравнение эффективности различных креативов для кампании{' '}
                <strong data-id="element-12643">{selectedCampaign.id}</strong>.
              </p>

              <div className="space-y-4" data-id="element-12644">
                {selectedCampaign.creatives?.map((cr: any, idx: number) => <div key={cr.id} className={`p-4 rounded-xl border ${idx === 0 ? 'border-emerald-400 bg-emerald-50/30' : 'border-slate-200 bg-white'} flex items-center justify-between`} data-id="element-12645">
                    <div className="flex items-center gap-4" data-id="element-12646">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${idx === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-12647">
                        {idx === 0 ? 'A' : 'B'}
                      </div>
                      <div data-id="element-12648">
                        <div className="font-bold text-slate-900" data-id="element-12649">
                          {cr.name}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-12650">
                          ID: {cr.id}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-6 text-center" data-id="element-12651">
                      <div data-id="element-12652">
                        <div className="text-[10px] text-slate-500 uppercase" data-id="element-12653">
                          CTR
                        </div>
                        <div className={`font-bold ${idx === 0 ? 'text-emerald-600' : 'text-slate-900'}`} data-id="element-12654">
                          {cr.ctr}
                        </div>
                      </div>
                      <div data-id="element-12655">
                        <div className="text-[10px] text-slate-500 uppercase" data-id="element-12656">
                          CR
                        </div>
                        <div className={`font-bold ${idx === 0 ? 'text-emerald-600' : 'text-slate-900'}`} data-id="element-12657">
                          {cr.cr}
                        </div>
                      </div>
                    </div>
                    {idx === 0 && <Badge variant="success" data-id="element-12658">Winner</Badge>}
                  </div>)}
              </div>

              <div className="mt-6 flex justify-end" data-id="element-12659">
                <Button variant="outline" onClick={() => setActiveModal(null)} data-id="element-12660">
                  Закрыть
                </Button>
              </div>
            </div>
          </Card>}
      </div>;
  };
  return <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-12661">
      {renderKpis()}
      {renderAiRecommendations()}
      {renderTable()}
      {renderModals()}
    </div>;
}